import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/UnifiedAuthContext';
import { ArrowLeft, Camera, X, Plus } from 'lucide-react';
import './Profile.scss';

const Profile = () => {
  const navigate = useNavigate();
  const { technician, updateProfile, urlParams } = useAuth();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    bio: '',
    profilePhoto: '',
    languages: [],
    certifications: []
  });

  const [newLanguage, setNewLanguage] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });
  const [previewImage, setPreviewImage] = useState('');

  const buildUrlWithParams = (path) => {
    const searchParams = new URLSearchParams();
    if (urlParams.location) searchParams.set('location', urlParams.location);
    if (urlParams.user) searchParams.set('user', urlParams.user);
    
    // Preserve integration parameter for GoHighLevel users
    if (urlParams.integration) searchParams.set('integration', urlParams.integration);
    
    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}` : path;
  };

  useEffect(() => {
    if (technician) {
      setFormData({
        bio: technician.bio || '',
        profilePhoto: technician.profilePhoto || '',
        languages: technician.languages || [],
        certifications: technician.certifications || []
      });
      setPreviewImage(technician.profilePhoto || '');
    }
  }, [technician]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is JPEG
      if (!file.type.includes('jpeg') && !file.type.includes('jpg')) {
        setMessage({ type: 'error', content: 'Only JPEG images are allowed' });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', content: 'Image size should be less than 5MB' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setFormData({ ...formData, profilePhoto: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };


  const addLanguage = () => {
    if (newLanguage.trim() && !formData.languages.includes(newLanguage.trim())) {
      setFormData({
        ...formData,
        languages: [...formData.languages, newLanguage.trim()]
      });
      setNewLanguage('');
    }
  };

  const removeLanguage = (index) => {
    setFormData({
      ...formData,
      languages: formData.languages.filter((_, i) => i !== index)
    });
  };

  const addCertification = () => {
    if (newCertification.trim() && !formData.certifications.includes(newCertification.trim())) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, newCertification.trim()]
      });
      setNewCertification('');
    }
  };

  const removeCertification = (index) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', content: '' });

    try {
      await updateProfile(formData);
      setMessage({ type: 'success', content: 'Profile updated successfully!' });
      setTimeout(() => {
        navigate(buildUrlWithParams('/dashboard'));
      }, 2000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        content: error.response?.data?.message || 'Failed to update profile' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    if (!technician?.name) return '';
    return technician.name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <button 
          className="back-button"
          onClick={() => navigate(buildUrlWithParams('/dashboard'))}
        >
          <ArrowLeft />
          Back to Dashboard
        </button>
        <h1>Edit Profile</h1>
      </div>

      <form onSubmit={handleSubmit} className="profile-form">
        {message.content && (
          <div className={`message ${message.type}`}>
            {message.content}
          </div>
        )}

        <div className="profile-section">
          <h2>Profile Photo</h2>
          <div className="photo-upload-container">
            <div className="current-photo">
              {previewImage ? (
                <img src={previewImage} alt="Profile" />
              ) : (
                <div className="photo-placeholder">
                  {getInitials()}
                </div>
              )}
            </div>
            <div className="upload-controls">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,.jpg,.jpeg"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="upload-button"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera />
                {previewImage ? 'Change Photo' : 'Upload Photo'}
              </button>
              <p className="upload-hint">Max size: 5MB JPEG images</p>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h2>Bio</h2>
          <textarea
            value={formData.bio}
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            placeholder="Tell us about yourself, your experience, and specialties..."
            rows={4}
            maxLength={500}
          />
          <div className="char-count">{formData.bio.length}/500</div>
        </div>

        <div className="profile-section">
          <h2>Languages</h2>
          <div className="list-input-container">
            <div className="input-add-group">
              <input
                type="text"
                value={newLanguage}
                onChange={(e) => setNewLanguage(e.target.value)}
                placeholder="Add a language"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
              />
              <button
                type="button"
                onClick={addLanguage}
                className="list-input__add"
                disabled={!newLanguage.trim()}
              >
                <Plus />
              </button>
            </div>
            <div className="tags-list">
              {formData.languages.map((lang, index) => (
                <span key={index} className="tags__item">
                  {lang}
                  <button
                    type="button"
                    onClick={() => removeLanguage(index)}
                    className="tag-remove"
                  >
                    <X />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h2>Certifications</h2>
          <div className="list-input-container">
            <div className="input-add-group">
              <input
                type="text"
                value={newCertification}
                onChange={(e) => setNewCertification(e.target.value)}
                placeholder="Add a certification"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
              />
              <button
                type="button"
                onClick={addCertification}
                className="list-input__add"
                disabled={!newCertification.trim()}
              >
                <Plus />
              </button>
            </div>
            <div className="tags-list">
              {formData.certifications.map((cert, index) => (
                <span key={index} className="tags__item">
                  {cert}
                  <button
                    type="button"
                    onClick={() => removeCertification(index)}
                    className="tags__remove"
                  >
                    <X />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={() => navigate(buildUrlWithParams('/dashboard'))}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="save-button"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Profile;
